import { z } from 'zod'
import { BlacklineSchema } from './schemas'

// ------ Agenda  -------------------------------------------------

export const AgendaSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  agenda_column_positions: z.array(z.number()),
  number_styles: z.array(z.string()),
  number_resets: z.array(z.number().nullable()),
  always_nest_numbers: z.boolean(),
})

// ----- Default Columns ------------------------------------------
export const TitleCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z.string().nullable(),
})

export const VersionCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z
    .object({
      dms: z
        .object({
          doc_id: z.string().nullable(),
          status: z.string().nullable(),
          current_version: z.number().nullable(),
        })
        .nullable(),
      closing_version: z
        .object({
          state: z.string(),
          ctype: z.string(),
        })
        .nullable(),
      latest_version: z
        .object({
          is_final: z.boolean(),
          firm: z.string().nullable(),
          date_created: z.string(),
          number: z.number().nullable(),
        })
        .nullable(),
    })
    .nullable(),
})

export const AccessCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z.object({
    deal_owner: z.string(),
    external_firms: z.array(z.string()).nullable(),
    is_public: z.boolean(),
  }),
})

export const ActionItemCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z
    .object({
      colour: z.number().nullable(),
      title: z.string().nullable(),
    })
    .nullable(),
})

export const NoteCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z
    .object({
      unresolved_notes_count: z.number(),
      latest_note: z.object({
        body: z.string(),
        created_by: z.string(),
        created_by_id: z.number(),
        date_created: z.string(),
        last_updated: z.string(),
      }),
    })
    .nullable(),
})

export const SignatureCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z
    .object({
      fraction: z.string().nullable(),
      state: z.string().nullable(),
      needs_review: z.boolean(),
    })
    .nullable(),
})

export const ScheduleCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z
    .object({
      state: z.string().nullable(),
      fraction: z.string().nullable(),
    })
    .nullable(),
})

export const ResponsibilityCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z.array(z.string()).nullable(),
})

// ----- Custom Columns -------------------------------------------------

export const CustomEntryCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z.string().nullable(),
})

export const DateCellDataSchema = CustomEntryCellDataSchema
export const TextCellDataSchema = CustomEntryCellDataSchema
export const NumberCellDataSchema = CustomEntryCellDataSchema
export const DropdownCellDataSchema = CustomEntryCellDataSchema
export const StatusDropdownCellDataSchema = CustomEntryCellDataSchema

export const AgendaObjectCBSchema = z.object({
  id: z.number(),
  content_id: z.number(),
  content_type: z.enum(['category', 'item', 'action-item']),
  gas_is_redacted: z.boolean(),
  number_level: z.number(),
  position: z.number(),
  column_data: z.record(
    z.string(),
    z.object({
      value: z
        .union([
          z.string(),
          // Version Column
          z.object({
            dms: z.unknown().nullable(),
            closing_version: z
              .object({
                state: z.string(),
                ctype: z.string(),
              })
              .nullable(),
            latest_version: z
              .object({
                is_final: z.boolean(),
                firm: z.string().nullable(),
                date_created: z.string(),
                number: z.number().nullable(),
              })
              .nullable(),
          }),
          // Action Item Column
          z.object({
            colour: z.number().nullable(),
            title: z.string().nullable(),
          }),
          // Notes Column
          z.object({
            unresolved_notes_count: z.number(),
            latest_note: z.object({
              body: z.string(),
              created_by: z.string(),
              date_created: z.string(),
              last_updated: z.string(),
              created_by_id: z.number(),
            }),
          }),
          // Access Control Column
          z.object({
            deal_owner: z.string(),
            external_firms: z.array(z.string()).nullable(),
            is_public: z.boolean(),
          }),
          // Signature Column
          z.object({
            fraction: z.string().nullable(),
            state: z.string().nullable(),
            needs_review: z.boolean(),
          }),
          // Schedule Column
          z.object({
            state: z.string().nullable(),
            fraction: z.string().nullable(),
          }),
          // Responsibility Column
          z.array(z.string()),
        ])
        .nullable(),
      model_ids: z.array(z.number()).nullable().optional(),
    }),
  ),
  number: z.string().nullable().optional(),
  hasAccess: z.boolean().optional(),
  closingBookDisabled: z.boolean().optional(),
  documentExtension: z.string().nullable().optional(),
})

export const AgendaObjectServerSchema = z.object({
  id: z.number(),
  content_id: z.number(),
  content_type: z.enum(['category', 'item', 'action-item']),
  gas_is_redacted: z.boolean(),
  number_level: z.number(),
  position: z.number(),
  column_data: z.record(
    z.string(),
    z.union([
      TitleCellDataSchema,
      ResponsibilityCellDataSchema,
      VersionCellDataSchema,
      ActionItemCellDataSchema,
      SignatureCellDataSchema,
      ScheduleCellDataSchema,
      NoteCellDataSchema,
      AccessCellDataSchema,
      TextCellDataSchema,
      NumberCellDataSchema,
      DateCellDataSchema,
    ]),
  ),
})

export const ContentTypesSchema = AgendaObjectServerSchema.shape.content_type

export const AgendaColumnSchema = z.object({
  id: z.number(),
  is_visible: z.boolean(),
  column_type: z.enum([
    'title',
    'responsibility',
    'schedule',
    'signature',
    'version',
    'note',
    'access',
    'text',
    'date',
    'number',
    'dropdown',
    'status-dropdown',
  ]),
  width: z.number(),
  column_title: z.string(),
  options: z
    .array(
      z.object({
        id: z.number(),
        label: z.string(),
        color: z.number(),
        status_category: z.enum(['open', 'in-progress', 'closed']).nullable().optional(),
      }),
    )
    .optional(),
})

export const AgendaColumnEntrySchema = z.object({
  agenda_column_id: z.number(),
  agenda_object_id: z.number(),
  id: z.number(),
  value: z.string(),
})

// ----- Bulk / Agenda Topbar Payloads ------------------------------------

export const BulkMarkFinalPayloadSchema = z.object({
  version_ids: z.array(z.number()),
})

export const BulkCompilePageRangeSchema = z.object({
  document_id: z.number(),
  document_type: z.string(),
  start_page: z.number(),
  end_page: z.number().nullable(),
})

export const BulkCompilePageRangeResponseSchema = z.object({
  document_id: z.number(),
  document_type: z.number(),
  start_page: z.number(),
  end_page: z.number().nullable(),
})

export const BulkCompileItemSchema = z.object({
  item_id: z.number(),
  page_ranges: z.array(BulkCompilePageRangeSchema),
})

export const BulkCompileItemResponseSchema = z.object({
  item_id: z.number(),
  page_ranges: z.array(BulkCompilePageRangeResponseSchema),
})

export const BulkCompileClosingVersionPayloadSchema = z.object({
  items: z.array(BulkCompileItemSchema),
})

export const CreateNotePayloadSchema = z.object({
  agenda_note: z.object({
    body: z.string(),
    content_type: z.enum(['item', 'action-item']),
    deal_id: z.number(),
    created_by_id: z.number(),
    item_id: z.number().nullable(),
    action_item_id: z.number().nullable(),
    created_by_name: z.null(),
    date_created: z.null(),
    is_resolved: z.boolean(),
    last_updated: z.null(),
  }),
})

export const CreateSnapshotPayloadSchema = z.object({
  snapshot: z.object({
    deal_id: z.number(),
    label: z.string(),
    create_content_as_group_id: z.number().optional(),
    is_failed: z.boolean(),
    gas_group_ids: z.array(z.number()),
    gas_is_public: z.boolean(),
    orientation: z.enum(['landscape', 'portrait']),
  }),
})

export const CreateBlacklinePayloadSchema = z.object({
  deal_id: z.number(),
  content_type: BlacklineSchema.shape.content_type,
  old_id: z.number(),
  new_id: z.number(),
})

export const UpdateNumberingPayloadSchema = z.object({
  always_nest_numbers: z.boolean(),
  number_styles: z.array(z.string()),
  number_resets: z.array(z.number().nullable()),
})

export const SetAgendaFirmsPayloadSchema = z.object({
  firm_ids: z.array(z.number()),
  item_ids: z.array(z.number()),
  action_item_ids: z.array(z.number()),
  replace: z.boolean(),
})

export const SetItemsCtypePayloadSchema = z.object({
  item_ids: z.array(z.number()),
  ctype: z.enum(['counterpart', 'original']),
})

export const AgendaObjectConversionPayloadSchema = z.object({
  item_ids: z.array(z.number()),
  action_item_ids: z.array(z.number()),
  category_ids: z.array(z.number()),
  convert_type: ContentTypesSchema,
})

export const SerializedAgendaObjectSchema = z.object({
  content_type: ContentTypesSchema,
  number_level: z.number(),
  content_payload: z.object({
    title: z.string(),
    note: z.string().nullable(),
    responsible_firm_ids: z.array(z.number()),
    gas_group_ids: z.array(z.number()),
    gas_is_public: z.boolean(),
    custom_columns: z.array(
      z.object({
        id: z.number(),
        value: z.string(),
      }),
    ),
  }),
})

export const BulkAddAgendaObjectsPayloadSchema = z.object({
  agenda_objects: z.array(SerializedAgendaObjectSchema),
  batched: z.number(),
  post_agenda_object_id: z.number().nullable(),
  pre_agenda_object_id: z.number().nullable(),
})

export const DeleteAgendaColumnsPayloadSchema = z.object({
  deleted_agenda_columns: z.array(z.number()),
})

export const CreateAgendaColumnsPayloadSchema = z.object({
  agenda_columns: z.array(
    z.object({
      column_type: z.enum(['text', 'date', 'number']),
      column_title: z.string(),
    }),
  ),
})

export const ModifyAgendaColumnsPayloadSchema = z.object({
  agenda_columns: z.array(
    z.object({
      id: z.number().optional(),
      column_title: z.string(),
      column_type: AgendaColumnSchema.shape.column_type,
      error: z.string(),
      is_visible: z.boolean(),
      options: z
        .array(
          z.object({
            id: z.number().optional(),
            label: z.string(),
            color: z.number(),
            status_category: z.enum(['open', 'in-progress', 'closed']).nullable().optional(),
          }),
        )
        .optional(),
    }),
  ),
})

// --- Table Actions ----------

export const UpdatePositionsPayloadSchema = z.object({
  agenda_object_ids: z.array(z.number()),
  pre_agenda_object_id: z.number().nullable(),
  post_agenda_object_id: z.number().nullable(),
})

export const RerankAgendaObjectsPayloadSchema = z.object({
  action: z.enum(['promote', 'demote']),
  agenda_object_ids: z.array(z.number()),
})
